import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import PatientCompleteWizardStep1 from './PatientCompleteWizardStep1';
import PatientCompleteWizardStep2 from './PatientCompleteWizardStep2';
import PatientCompleteWizardStep3 from './PatientCompleteWizardStep3';
import PatientCompleteWizardStep4 from './PatientCompleteWizardStep4';
import PatientCompleteWizardStep5 from './PatientCompleteWizardStep5';

import { Alert, Button, Stack, Step, StepLabel, Stepper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { completePatientWithInclusionInfo, fetchPatient, PatientInclusionInfo } from '../../store/patientsSlice';

import { trackEvent, TrackingEventTypes } from '../../common/tracking.ts';
import { PageBox } from '../ui/DaliaComponents.tsx';
import { PatientCompleteWizardType } from '../ui/WizardStep.tsx';

const steps: PatientCompleteWizardType[] = [
  PatientCompleteWizardStep1,
  PatientCompleteWizardStep2,
  PatientCompleteWizardStep3,
  PatientCompleteWizardStep4,
  PatientCompleteWizardStep5,
];

export const PatientCompleteWizard = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { id, step } = useParams<{ id: string; step: string }>();
  const correctedStep = Math.min(Math.max(Number(step) || 0, 0), steps.length - 1);
  const [currentStep, setCurrentStep] = useState(correctedStep);
  const [updateMode, setUpdateMode] = useState(false);
  const { patientEdited, error, loading } = useSelector((state: RootState) => state.patients);
  const needsRefetch = useMemo(() => !patientEdited || patientEdited.id !== id, [patientEdited, id]);

  // commulate the initial values of all steps
  const iv: Partial<PatientInclusionInfo> = steps.reduce(
    (values, { initialValues }) => ({
      ...values,
      ...initialValues,
    }),
    {},
  );
  const [initialValues, setInitialValues] = useState(iv);

  useEffect(() => {
    if (id && needsRefetch) {
      void dispatch(fetchPatient({ id: id }));
    }
  }, [id, dispatch, needsRefetch]);

  useEffect(() => {
    // Update the URL whenever the currentStep changes
    if (patientEdited) {
      navigate(`/patient/${patientEdited.id}/complete/${currentStep.toString()}`);
    }
  }, [currentStep, patientEdited, navigate]);

  // if we load a patient from the back end and inclusionInfo is already completed, we are in the update mode
  // in this case we only display the selected step
  useEffect(() => {
    if (patientEdited) {
      // update mode
      const um = patientEdited.inclusionInfo != null;
      setUpdateMode(um);
      if (um && patientEdited.inclusionInfo) {
        setInitialValues(patientEdited.inclusionInfo);
      } else {
        trackEvent(TrackingEventTypes.ADD_PATIENT_INFO_STARTED, { patient_id: patientEdited.id });
      }
    }
  }, [patientEdited]);

  const nextStep = () => {
    setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
  };

  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 0));
  };

  const isLastStep = () => {
    return currentStep === steps.length - 1;
  };

  const exitPage = () => {
    if (updateMode && patientEdited?.id) navigate(`/patient/${patientEdited.id}/view/inclusion`);
    else {
      trackEvent(TrackingEventTypes.ADD_PATIENT_INFO_COMPLETE, { patient_id: patientEdited?.id });
      navigate('/home');
    }
  };

  const onSubmit = async (values: Partial<PatientInclusionInfo>) => {
    // if we are not in the last step, we go to the next step
    // if we are in update mode only this step is to be saved
    if (!isLastStep() && !updateMode) {
      nextStep();
      return;
    }
    if (patientEdited?.id) {
      // update inclusion info
      const patientInclusionData = {
        id: patientEdited.id,
        inclusionInfo: { ...values },
      };
      const resultAction = await dispatch(completePatientWithInclusionInfo(patientInclusionData));
      if (completePatientWithInclusionInfo.fulfilled.match(resultAction)) {
        // Navigate after the API call completes successfully
        exitPage();
      }
    }
  };

  const ActiveStep = steps[currentStep];
  const validationSchema = ActiveStep.validationSchema;

  return (
    <PageBox title={updateMode ? ActiveStep.label : 'Fiche médicale '}>
      {error && (
        <Stack direction="column">
          <Alert severity="error">{error}</Alert>
          <Button onClick={exitPage} variant="text">
            Annuler
          </Button>
        </Stack>
      )}
      {!error && !loading && (
        <Formik
          enableReinitialize={true} // Important to allow reinitializing
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {(formik) => (
            <Form noValidate>
              {!updateMode && (
                <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 5 }}>
                  {steps.map((_, index) => {
                    return (
                      <Step completed={currentStep > index} key={index}>
                        <StepLabel>{steps[index].label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              )}

              <ActiveStep formik={formik} />
              <Grid container sx={{ mt: 4 }}>
                <Grid size={4}>
                  {currentStep > 0 && !updateMode ? (
                    <Button onClick={prevStep} variant="outlined">
                      Précédent
                    </Button>
                  ) : (
                    <div />
                  )}
                </Grid>
                <Grid size={8}>
                  <Grid container justifyContent="flex-end">
                    <Button onClick={exitPage} variant="text">
                      Annuler
                    </Button>
                    <Button type="submit" variant="contained">
                      {isLastStep() || updateMode ? 'Enregistrer' : 'Suivant'}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </PageBox>
  );
};

export default PatientCompleteWizard;
